import { z } from "zod";
import { ComputedPropertiesSchema, PaywallResponseSchema } from "./paywall";
const TriggerV1 = z.object({
    event_name: z.string(),
    trigger_version: z.literal("V1")
});
const VariantHoldout = z.object({
    variant_type: z.literal("HOLDOUT"),
    variant_id: z.string()
});
const VariantPaywall = z.object({
    variant_type: z.literal("TREATMENT"),
    variant_id: z.string(),
    paywall_identifier: z.string()
});
const Variant = z.union([
    VariantHoldout,
    VariantPaywall
]);
const TriggerV2 = z.object({
    event_name: z.string(),
    trigger_version: z.literal("V2"),
    rules: z.array(z.object({
        experiment_group_id: z.string(),
        experiment_id: z.string(),
        expression: z.union([
            z.string(),
            z.null()
        ]),
        expression_js: z.union([
            z.string(),
            z.null()
        ]),
        expression_cel: z.union([
            z.string(),
            z.null()
        ]),
        assigned: z.boolean(),
        variant: Variant
    }))
});
const Trigger = z.union([
    TriggerV1,
    TriggerV2
]);
const SharedConfigSchema = z.object({
    toggles: z.array(z.object({
        key: z.string(),
        enabled: z.boolean()
    })),
    product_identifier_groups: z.array(z.array(z.string())),
    paywalls: z.array(z.object({
        identifier: z.string(),
        products: z.array(z.object({
            identifier: z.string()
        }))
    })),
    log_level: z.number(),
    localization: z.object({
        locales: z.array(z.object({
            locale: z.string()
        }))
    }),
    postback: z.object({
        delay: z.number(),
        products: z.array(z.object({
            identifier: z.string(),
            platform: z.string()
        }))
    }),
    app_session_timeout_ms: z.number(),
    tests: z.object({
        dns_resolution: z.array(z.object({
            hostname: z.string()
        }))
    })
});
export const ConfigResponseSchema = SharedConfigSchema.extend({
    triggers: z.array(Trigger)
});
export const StaticVariantHoldout = VariantHoldout.extend({
    percentage: z.number()
});
const StaticVariantPaywall = VariantPaywall.extend({
    percentage: z.number()
});
export const StaticVariant = z.union([
    StaticVariantHoldout,
    StaticVariantPaywall
]);
export const ReferencedProperty = z.object({
    // Ex: `['user', 'is_subscribed']`
    path: z.array(z.string())
});
// This is how the SDK knows if we need to preload an experiment or not.
// By default, we'll return `ALWAYS` for rules that reference `user` or `params`
// b/c we those frequently change, but we'll return `IF_TRUE` for rules that
// reference other properties b/c those don't change as frequently.
// Ex: `device.os == 'iOS'` will return `IF_TRUE` b/c `device.os` doesn't change
// EX: `user.most_recent_purchase_date > 1 week ago` will return `ALWAYS` b/c
// `user.most_recent_purchase_date` changes frequently
export const TriggerExperimentPreloadBehavior = z.union([
    z.literal("ALWAYS"),
    z.literal("IF_TRUE"),
    z.literal("NEVER")
]);
// This is how the SDK knows if we need to preload an experiment or not.
export const TriggerExperimentPreload = z.object({
    behavior: TriggerExperimentPreloadBehavior
});
export const StaticTriggerV2 = z.object({
    event_name: z.string(),
    trigger_version: z.literal("V2"),
    rules: z.array(z.object({
        experiment_group_id: z.string(),
        experiment_id: z.string(),
        expression: z.union([
            z.string(),
            z.null()
        ]),
        expression_js: z.union([
            z.string(),
            z.null()
        ]),
        expression_cel: z.union([
            z.string(),
            z.null()
        ]),
        variants: z.array(StaticVariant),
        // The properties we need to inject into the expression context to
        // evaluate the expression properly
        computed_properties: ComputedPropertiesSchema,
        // These are all the properties that we're referencing in the expression
        // that we need to inject into the expression context & that we need to
        // watch for changes to, they will only be one level deep
        // Ex: [ { path: ['user', 'is_subscribed'] } } ]
        // Ex: [ { path: ['user', 'nested' ] }] This will indicate that we need to
        // watch for any change to the `user`.`nested` object
        // Referenced properties will also be included in this list
        referenced_properties: z.array(ReferencedProperty),
        // This is how the SDK knows if we need to preload an experiment or not.
        // The `ALWAYS` behavior is implictly what we had been doing before we
        // added this field, so we'll default to that for now.
        preload: TriggerExperimentPreload
    }))
});
const Web2AppConfig = z.object({
    entitlements_max_age_ms: z.number(),
    restore_access_url: z.string()
});
const StaticTrigger = z.union([
    TriggerV1,
    StaticTriggerV2
]);
export const StaticConfigSchema = SharedConfigSchema.extend({
    build_id: z.string(),
    ts: z.number(),
    web2app_config: Web2AppConfig.nullable(),
    attribution_options: z.object({
        apple_search_ads: z.object({
            enabled: z.boolean()
        })
    }),
    products: z.array(z.object({
        store_product: z.object({
            store: z.string(),
            product_identifier: z.string(),
            base_plan_identifier: z.string().optional(),
            offer: z.object({
                type: z.string(),
                offer_identifier: z.string().optional()
            }).optional()
        }),
        entitlements: z.array(z.object({
            identifier: z.string(),
            type: z.string()
        }))
    })),
    trigger_options: z.array(StaticTrigger),
    paywall_responses: z.array(PaywallResponseSchema),
    disable_preload: z.object({
        triggers: z.array(z.object({
            event_name: z.string()
        })),
        all: z.boolean()
    })
});
